import React from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import AdvisorAvatar, { advisorThemes, advisorThemesDesktopSize } from '../../../advisor_avatar';
import AdvisorModes from '../advisor_modes';
import PriceText from '../price_text';
import classes from './classes.module.scss';
import AppLink from '../../../routes/app_link';
import {
  isXfmProgram, subscribedToAdvisor, getCountryFlagUrl
} from '../../../../config/util';
import { withAnalytics } from './advisor_preview_with_analytics';
import { isKA, isPS, isWebView } from '../../../../../common/config/utils';
import { SvgIcon } from '../../../../../common/sprites';
import { handleNotifyClick } from '../../../advisor_profile_avatar/top_icons/top_icons_utils';
import { renderFreeMinutesLabel, renderPsiquicosFreeMinutesLabel } from '../nested_views_utils';
import { useNotification } from '../../../firebase_messaging_provider';
import { AdvisorPreviewService, PsiquicosAdvisorPreviewService } from './advisor_preview_service';

// const maxServiceDescriptionChars = 100;

const getadvisorSlug = (advisorSlug) => {
  if (advisorSlug !== undefined) return advisorSlug;
  return '';
};

const renderAdvisorModes = (loading, advisor, text, showPrice = true) => (
  <AdvisorModes { ...advisor } loading={ loading } text={ text } showPrice={ showPrice } />
);

const renderPriceText = (loading, advisor) => {
  if (loading) return <Skeleton width={ 70 } />;
  return <PriceText { ...advisor } />;
};

const getKey = (advisor) => (advisor || {}).id;

// const getServiceDescriptionRowsCount = () => {
//   if (isMobile()) return 3;
//   return 4;
// };

const handleLinkClickAction = ({
  e, loading, sectionSlug, updateNavigationStack
}) => {
  if (loading) {
    e.preventDefault();
    return;
  }
  if (sectionSlug) { updateNavigationStack({ dataKey: sectionSlug }); }
};

const getNotifyIconId = () => (isWebView() ? 'notify_icon_disabled' : 'notify_icon');

const renderNotifyMeButton = (onNotifyClick, availabilityNotification) => (
  <button disabled={ isWebView() } style={ isWebView() ? { backgroundColor: '#EAEAEA', color: '#A1A1A1' } : {} } type="button" onClick={ onNotifyClick } className={ classes.notifyMeButton }>
    <SvgIcon id={ subscribedToAdvisor(availabilityNotification) ? 'notify_set_icon' : getNotifyIconId() } size={ [16, 18] } />
    {subscribedToAdvisor(availabilityNotification) ? 'Notification set' : 'Notify me'}
  </button>
);
const getStartCallMode = (liveModes) => {
  if (!liveModes ||  !liveModes.find(m => m.modeState === 'online')) return null;
  if (liveModes.find(m => m.mode === 'chat').modeState === 'online') return liveModes.find(m => m.mode === 'chat');
  return liveModes.find(m => m.mode === 'voice');
};

export const renderStartCallButton = (advisor, displayNotifyMe, authPendingAction, displayRegister, location, analytics, handleLinkClick, t) => {
  if (!isKA()) return null;
  const {
    id, liveModes, availabilityNotification, slug
  } = advisor;
  const onNotifyClick = (e) => handleNotifyClick(e, displayNotifyMe, id, authPendingAction, displayRegister, location, false);
  const mode = getStartCallMode(liveModes);
  return mode ? (
    // start call from advisor preview //
    // <AdvisorCta
    //   { ...advisor }
    //   { ...mode }
    //   loading={ loading }
    //   advisorId={ id }
    //   advisorCallProps={ {
    //     id,
    //     name: nickname,
    //     profilePictureUrl
    //   } }
    //   liveModes={ liveModes }
    //   advisorXfmProgram={ xfmProgram }
    //   type="advisorPreviewCta"
    // />
    <AppLink
      to={ {
        pathname: getadvisorSlug(slug),
        state: {  ...analytics, startChatClickSource: (analytics || {}).clickSource || (analytics || {})['click source'] }
      } }
      className={ classes.previewAdvisorCTA }
      onClick={ handleLinkClick }
    >
      {t('advisor_profile.connect')}
    </AppLink>
  ) : renderNotifyMeButton(onNotifyClick, availabilityNotification);
};

const renderNotifyButton = (advisor, displayNotifyMe, authPendingAction, displayRegister, location) => {
  if (!isKA()) return null;
  const {
    id, liveModes, availabilityNotification
  } = advisor;
  const onNotifyClick = (e) => handleNotifyClick(e, displayNotifyMe, id, authPendingAction, displayRegister, location, false);
  const mode = getStartCallMode(liveModes);
  if (mode) return null;
  return (
    <div className={ classes.previewAdvisorCTAContainer }>
      {renderNotifyMeButton(onNotifyClick, availabilityNotification)}
    </div>
  );
};

const renderNotifyMeButtonContainer = (advisor, displayNotifyMe, authPendingAction, displayRegister, location, analytics, handleLinkClick, t) => (isKA()
  ? renderNotifyButton(advisor, displayNotifyMe, authPendingAction, displayRegister, location, analytics, handleLinkClick, t)
  : null);

const adjustModesInfo = (valueWithoutModes, valueWithModes, advisor) => {
  if (!isKA() || advisor?.liveModes?.some(mode => mode.modeState === 'online')) return valueWithModes;
  return valueWithoutModes;
};

const renderModesInfo = ({
  renderAdvisorFreeMinutes, loading, advisor, t
}) => (
  <div className={ classes.modesInfoContainerWithIcons }>
    {renderAdvisorFreeMinutes}
    {adjustModesInfo(
      renderPriceText(loading, advisor),
      renderAdvisorModes(loading, advisor, t('home_page.from'), !isPS()),
      advisor
    )}
  </div>
);

function AdvisorPreview({
  theme = 'badge',
  advisor,
  advisorCount = 1,
  loading,
  analytics,
  content,
  style,
  className,
  updateNavigationStack,
  sectionSlug,
  onAnalyticsClick,
  innerRef,
  t,
  userXfmProgram = {},
  displayNotifyMe,
  authPendingAction,
  displayRegister
}) {
  const location = useLocation();
  const { trackAdvisorVisit } = useNotification();

  const renderAdvisorFreeMinutes = React.useMemo(() => {
    if (!isXfmProgram({ advisorXfmProgram: advisor?.xfmProgram, userXfmProgram }) || isPS()) return null;
    return renderFreeMinutesLabel(t);
  }, [advisor?.xfmProgram?.active, userXfmProgram?.credits, t]);

  const psiquicosFreeMinutesBadge = React.useMemo(() => {
    if (!isPS() || !isXfmProgram({ advisorXfmProgram: advisor?.xfmProgram, userXfmProgram })) return null;
    return renderPsiquicosFreeMinutesLabel(t);
  }, [advisor?.xfmProgram?.active, userXfmProgram?.credits, t]);

  const renderAdvisorService = () => (
    isPS()
      ? (
        <PsiquicosAdvisorPreviewService
          advisor={ advisor }
          loading={ loading }
          t={ t }
          freeMinutesBadge={ psiquicosFreeMinutesBadge }
        />
      )
      : <AdvisorPreviewService advisor={ advisor } loading={ loading } />
  );

  const handleLinkClick = (e) => {
    if (onAnalyticsClick) { onAnalyticsClick(); }
    trackAdvisorVisit();
    handleLinkClickAction({
      e, loading, sectionSlug, updateNavigationStack
    });
  };

  const renderBottomSection = () => (content || (
    <div className={ classes.bottomSection }>
      {renderModesInfo({
        renderAdvisorFreeMinutes, loading, advisor, t
      })}
      {renderNotifyMeButtonContainer(advisor, displayNotifyMe, authPendingAction, displayRegister, location, analytics, handleLinkClick, t)}
    </div>
  ));

  const renderAdvisorContent = () => {
    if (content) return content;
    return (
      <AppLink
        to={ {
          pathname: getadvisorSlug(advisor.slug),
          state: {  ...analytics, startChatClickSource: (analytics || {}).clickSource || (analytics || {})['click source'] }
        } }
        className={ classes.advisorViewLink }
        onClick={ handleLinkClick }
      >
        <AdvisorAvatar { ...advisor } desktopSize={ advisorThemesDesktopSize[theme] } theme={ advisorThemes[theme] } loading={ loading } analytics={ analytics  } countryFlagUrl={ getCountryFlagUrl(advisor?.countryCode) } showReadings={ !isPS() } />
        { renderAdvisorService() }
        {renderBottomSection()}
      </AppLink>
    );
  };

  const renderAdvisor = () => (
    <div key={ getKey(advisor) } className={ classNames(classes.advisorView, className) } style={ style } suppressHydrationWarning>
      { renderAdvisorContent() }
    </div>
  );

  return (
    <div className={ classes[theme] } style={ { '--advisorCount': `${ advisorCount }` } } ref={ innerRef } suppressHydrationWarning>
      { renderAdvisor() }
    </div>
  );
}

// wrap here in case someone wants to include it directly
export default withAnalytics(AdvisorPreview);
